import React, { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { AdminStatCard } from "./AdminStatCard";
import { toast } from "sonner";
import { AlertTriangle, DollarSign, CheckCircle, Plus, X, Save, Trash2, Pencil, FileText } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminGjobat = ({ cars, clients }) => {
  const [fines, setFines] = useState([]);
  const [stats, setStats] = useState({ total: 0, active: 0, paid: 0, total_amount: 0 });
  const [filter, setFilter] = useState("all");
  const [showModal, setShowModal] = useState(false);
  const [editingFine, setEditingFine] = useState(null);
  const [form, setForm] = useState({
    car_id: "", client_id: "", booking_id: "", date: new Date().toISOString().slice(0, 10),
    description: "", amount: "", reference: ""
  });

  useEffect(() => { loadFines(); loadStats(); }, [filter]);

  const loadFines = async () => {
    try {
      const token = localStorage.getItem("adminToken");
      const res = await axios.get(`${API}/admin/fines?status=${filter}`, { headers: { Authorization: `Bearer ${token}` } });
      setFines(res.data);
    } catch (e) { console.error(e); }
  };

  const loadStats = async () => {
    try {
      const token = localStorage.getItem("adminToken");
      const res = await axios.get(`${API}/admin/fines/stats`, { headers: { Authorization: `Bearer ${token}` } });
      setStats(res.data);
    } catch (e) { console.error(e); }
  };

  const openModal = (fine = null) => {
    if (fine) {
      setEditingFine(fine);
      setForm({ car_id: fine.car_id, client_id: fine.client_id || "", booking_id: fine.booking_id || "", date: fine.date, description: fine.description, amount: fine.amount, reference: fine.reference || "" });
    } else {
      setEditingFine(null);
      setForm({ car_id: "", client_id: "", booking_id: "", date: new Date().toISOString().slice(0, 10), description: "", amount: "", reference: "" });
    }
    setShowModal(true);
  };

  const saveFine = async () => {
    if (!form.car_id || !form.description || !form.amount || !form.date) {
      toast.error("Plotësoni fushat e detyrueshme"); return;
    }
    try {
      const token = localStorage.getItem("adminToken");
      if (editingFine) {
        await axios.put(`${API}/admin/fines/${editingFine.id}`, form, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Gjoba u përditësua");
      } else {
        await axios.post(`${API}/admin/fines`, { ...form, amount: parseFloat(form.amount) }, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Gjoba u regjistrua");
      }
      setShowModal(false);
      loadFines(); loadStats();
    } catch (e) { toast.error("Gabim"); console.error(e); }
  };

  const deleteFine = async (id) => {
    if (!window.confirm("Jeni të sigurt?")) return;
    try {
      const token = localStorage.getItem("adminToken");
      await axios.delete(`${API}/admin/fines/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Gjoba u fshi"); loadFines(); loadStats();
    } catch (e) { toast.error("Gabim"); }
  };

  const markPaid = async (fine) => {
    try {
      const token = localStorage.getItem("adminToken");
      await axios.put(`${API}/admin/fines/${fine.id}`, { status: "paid" }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Gjoba u shënua si e paguar"); loadFines(); loadStats();
    } catch (e) { toast.error("Gabim"); }
  };

  const getCarName = (id) => { const c = (cars || []).find(x => x.id === id); return c ? `${c.brand} ${c.model}` : id; };
  const getClientName = (id) => { const c = (clients || []).find(x => x.id === id); return c ? c.name : id; };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-white">Gjobat</h2>
        <Button onClick={() => openModal()} className="bg-yellow-600 hover:bg-yellow-500" data-testid="add-fine-btn">
          <Plus className="h-4 w-4 mr-2" /> Regjistro Gjobë
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <AdminStatCard value={stats.total} label="Gjithsej" color="yellow" icon={<FileText className="h-5 w-5" />} />
        <AdminStatCard value={stats.active} label="Aktive" color="red" icon={<AlertTriangle className="h-5 w-5" />} active={filter === "active"} onClick={() => setFilter(filter === "active" ? "all" : "active")} />
        <AdminStatCard value={stats.paid} label="Paguar" color="green" icon={<CheckCircle className="h-5 w-5" />} active={filter === "paid"} onClick={() => setFilter(filter === "paid" ? "all" : "paid")} />
        <AdminStatCard value={`€${stats.total_amount}`} label="Totali aktiv" color="orange" icon={<DollarSign className="h-5 w-5" />} />
      </div>

      <div className="flex gap-3 mb-6">
        <select value={filter} onChange={(e) => setFilter(e.target.value)} className="flex-1 bg-zinc-800 border border-gray-700 text-white rounded-md px-3 py-2">
          <option value="all">Të gjitha</option>
          <option value="active">Aktive</option>
          <option value="paid">Paguar</option>
          <option value="cancelled">Anulluar</option>
        </select>
      </div>

      {fines.length === 0 ? (
        <div className="text-center py-16 text-gray-500">
          <AlertTriangle className="h-12 w-12 mx-auto mb-3 opacity-30" />
          <p className="font-medium">Nuk ka gjoba të regjistruara</p>
        </div>
      ) : (
        <div className="space-y-3">
          {fines.map((f) => (
            <div key={f.id} className={`bg-zinc-900 border rounded-xl p-4 hover:border-gray-700 transition-colors ${f.status === "active" ? "border-red-600/30" : "border-gray-800"}`} data-testid={`fine-${f.id}`}>
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-white font-semibold">{f.description}</p>
                  <p className="text-gray-400 text-sm">{getCarName(f.car_id)}{f.client_id && ` | ${getClientName(f.client_id)}`}</p>
                  <p className="text-gray-500 text-xs mt-1">{f.date}{f.reference && ` | Ref: ${f.reference}`}</p>
                </div>
                <div className="text-right flex items-center gap-2">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${f.status === "active" ? "bg-red-600/20 text-red-400" : f.status === "paid" ? "bg-green-600/20 text-green-400" : "bg-gray-600/20 text-gray-400"}`}>
                    {f.status === "active" ? "Aktive" : f.status === "paid" ? "Paguar" : "Anulluar"}
                  </span>
                  <p className="text-yellow-500 font-bold text-lg">€{f.amount?.toFixed(2)}</p>
                  <div className="flex gap-1 ml-2">
                    {f.status === "active" && (
                      <Button size="sm" variant="ghost" onClick={() => markPaid(f)} className="text-green-500 hover:text-green-400" title="Shëno si paguar">
                        <CheckCircle className="h-4 w-4" />
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => openModal(f)} className="text-gray-400 hover:text-white"><Pencil className="h-4 w-4" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => deleteFine(f.id)} className="text-red-500 hover:text-red-400"><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <div className="bg-zinc-900 rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-white">{editingFine ? "Ndrysho Gjobën" : "Regjistro Gjobë"}</h3>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-white"><X className="h-5 w-5" /></button>
            </div>
            <div className="space-y-3">
              <div>
                <Label className="text-gray-300">ID Automjeti *</Label>
                <select value={form.car_id} onChange={(e) => setForm({...form, car_id: e.target.value})} className="w-full mt-1 bg-zinc-800 border border-gray-700 text-white rounded-md px-3 py-2" data-testid="fine-car-select">
                  <option value="">Zgjidh makinën...</option>
                  {(cars || []).map(c => <option key={c.id} value={c.id}>{c.brand} {c.model}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-gray-300">ID Klienti</Label>
                <select value={form.client_id} onChange={(e) => setForm({...form, client_id: e.target.value})} className="w-full mt-1 bg-zinc-800 border border-gray-700 text-white rounded-md px-3 py-2">
                  <option value="">Zgjidh klientin (opsionale)...</option>
                  {(clients || []).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-gray-300">ID Rezervimi</Label>
                <Input value={form.booking_id} onChange={(e) => setForm({...form, booking_id: e.target.value})} className="bg-zinc-800 border-gray-700 text-white mt-1" placeholder="Opsionale" />
              </div>
              <div>
                <Label className="text-gray-300">Data e gjobës *</Label>
                <Input type="date" value={form.date} onChange={(e) => setForm({...form, date: e.target.value})} className="bg-zinc-800 border-gray-700 text-white mt-1" data-testid="fine-date" />
              </div>
              <div>
                <Label className="text-gray-300">Përshkrimi *</Label>
                <Textarea value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} className="bg-zinc-800 border-gray-700 text-white mt-1" rows={2} data-testid="fine-description" />
              </div>
              <div>
                <Label className="text-gray-300">Shuma (€) *</Label>
                <Input type="number" value={form.amount} onChange={(e) => setForm({...form, amount: e.target.value})} className="bg-zinc-800 border-gray-700 text-white mt-1" placeholder="50.00" data-testid="fine-amount" />
              </div>
              <div>
                <Label className="text-gray-300">Nr. reference / biletë</Label>
                <Input value={form.reference} onChange={(e) => setForm({...form, reference: e.target.value})} className="bg-zinc-800 border-gray-700 text-white mt-1" />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <Button variant="outline" onClick={() => setShowModal(false)} className="border-gray-600 text-gray-300">Anulo</Button>
              <Button onClick={saveFine} className="bg-yellow-600 hover:bg-yellow-500"><Save className="h-4 w-4 mr-2" />Ruaj</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
